/**
 * TEC-294 (TEC-280, P1.6): el perfil de la instancia tal como lo manda el API en `me`
 * (`user.instance`, desde TEC-290) y la versión resuelta que lee el app.
 *
 * El contrato vive en el API: `App\Support\InstanceProfile::forApp()`, `App\Enums\Product`
 * (vocabulario por producto) y `App\Enums\FeatureFlag` (banderas). Si cambia allá, cambia aquí.
 *
 * Regla de TEC-280 (refactor invisible): sin `instance` (API anterior a TEC-290, el usuario del
 * SSO antes de refrescar `me`, o nadie con sesión) cada valor es el que el app usaba hasta hoy.
 */

export type Product = 'university' | 'corporate';

/** Claves estables del vocabulario del dominio (`App\Enums\Product::vocabulary()`). */
export type VocabularyKey = 'student' | 'school_period' | 'scholarship' | 'circuit';

/** Género gramatical de un término, para concordar los textos ("Beca aplicada" / "Subsidio aplicado"). */
export type Gender = 'f' | 'm';

export interface Term {
  singular: string;
  plural: string;
  /**
   * El API todavía no lo manda (`Product::vocabulary()` no modela el género): sale de
   * `PRODUCT_VOCABULARY` salvo que la respuesta traiga uno válido.
   */
  gender: Gender;
}

export type Vocabulary = Record<VocabularyKey, Term>;

/**
 * El vocabulario tal como lo manda el API: sin género, y un API anterior a TEC-291 no trae `circuit`.
 * Lo que falte sale de `PRODUCT_VOCABULARY`.
 */
export type VocabularyPayload = Partial<Record<VocabularyKey, Omit<Term, 'gender'> & { gender?: Gender }>>;

/**
 * Banderas de funcionalidad que el app conoce (`App\Enums\FeatureFlag`). Igual que en el API,
 * una bandera nueva entra sólo por aquí: una llave que el API mande y no esté en esta lista se
 * ignora.
 */
export type FeatureFlag = 'circuito_tec' | 'offline_scans' | 'shopping_cart' | 'wallet_visible' | 'ticket_packages';

export type Features = Record<FeatureFlag, boolean>;

export interface InstanceBrand {
  name: string;
  logo: string;
  logo_dark: string;
  favicon: string;
  primary_color: string;
}

export interface InstanceSso {
  /** Interruptor de app_settings.sso_enabled en el API. */
  enabled: boolean;
  idp: string;
  /** Texto del botón de SSO del login. */
  label: string;
}

/** `user.instance` tal como lo serializa el API. */
export interface InstanceProfile {
  product: Product;
  brand: InstanceBrand;
  vocabulary: VocabularyPayload;
  sso: InstanceSso;
  features: Features;
  /** TEC-489: días hacia adelante de la venta; null = sin definir. */
  days_to_book?: number | null;
}

/**
 * Lo que el app recibe en la práctica: puede faltar entero (APIs viejas) o venir incompleto. Las
 * banderas también: un API anterior a la bandera no la manda y vale su llave legada (o false).
 */
export type InstanceProfilePayload = Partial<Omit<InstanceProfile, 'features'>> & { features?: Partial<Features> };

/** Lo único que el store lee del usuario guardado en `me`. */
export interface TenantUser {
  instance?: InstanceProfilePayload | null;
  /** Sólo para las banderas legadas: `campus_actual.circuito_tec` (TEC-103). */
  campus_actual?: { circuito_tec?: boolean | number | null } | null;
}

/** El perfil resuelto que expone `TenantConfig`. */
export interface TenantProfile {
  product: Product;
  /**
   * null = el app conserva su marca empaquetada (nombre, íconos, splash, colores). El `brand` del
   * API es el del panel (p. ej. el nombre es su APP_NAME, "MovilidadTec") y no coincide con lo que
   * muestra el app, así que todavía nadie lo pinta.
   */
  brand: InstanceBrand | null;
  vocabulary: Vocabulary;
  sso: InstanceSso;
  features: Features;
  /** TEC-489: días hacia adelante de la venta; null = sin definir. */
  daysToBook: number | null;
}

/** Llave de Preferences donde el app guarda al usuario (`UserService`). */
export const ME_STORAGE_KEY = 'me';

/** Vocabulario por producto: copia de `App\Enums\Product::vocabulary()`, más el género de cada término. */
export const PRODUCT_VOCABULARY: Record<Product, Vocabulary> = {
  university: {
    student: { singular: 'Estudiante', plural: 'Estudiantes', gender: 'm' },
    school_period: { singular: 'Periodo Escolar', plural: 'Periodos Escolares', gender: 'm' },
    scholarship: { singular: 'Beca', plural: 'Becas', gender: 'f' },
    circuit: { singular: 'Circuito TEC', plural: 'Circuitos TEC', gender: 'm' },
  },
  corporate: {
    student: { singular: 'Colaborador', plural: 'Colaboradores', gender: 'm' },
    school_period: { singular: 'Periodo', plural: 'Periodos', gender: 'm' },
    scholarship: { singular: 'Subsidio', plural: 'Subsidios', gender: 'm' },
    // Provisional en el API (TEC-291): el nombre del módulo en Passenger es decisión de producto pendiente.
    circuit: { singular: 'Circuito', plural: 'Circuitos', gender: 'm' },
  },
};

/** El SSO de hoy: el del Tec, con el texto fijo que tenía `login.page.html`. */
export const DEFAULT_SSO: InstanceSso = {
  enabled: true,
  idp: 'tec',
  label: 'Soy Estudiante/Colaborador',
};

/** `App\Enums\FeatureFlag::default()`: lo que toma un usuario sin campus o sin perfil. */
export const DEFAULT_FEATURES: Features = {
  circuito_tec: false,
  // TEC-474: cola de escaneos sin red del conductor (TEC-299). Sólo llega por `instance.features`.
  offline_scans: false,
  shopping_cart: false,
  // TEC-489: se muestran salvo que la empresa los apague.
  wallet_visible: true,
  ticket_packages: true,
};

/** Movilidad Tec tal como se ve hoy, sin perfil de instancia. */
export const DEFAULT_TENANT_PROFILE: TenantProfile = {
  product: 'university',
  brand: null,
  vocabulary: PRODUCT_VOCABULARY.university,
  sso: DEFAULT_SSO,
  features: DEFAULT_FEATURES,
  daysToBook: null,
};

/**
 * De dónde leía el app cada bandera antes de TEC-290. Se usa cuando el perfil no trae
 * `features`: el API calcula ambas con la misma `FeaturePolicy` en la misma respuesta, así que
 * con o sin `instance` la bandera vale lo mismo que leía el app.
 */
const LEGACY_FEATURES: Record<FeatureFlag, (user: TenantUser) => boolean> = {
  circuito_tec: (user) => !!user.campus_actual?.circuito_tec,
  // Nació con el perfil de instancia: sin `instance` (API anterior a TEC-299) queda apagada, como hoy.
  offline_scans: () => false,
  shopping_cart: () => false,
  wallet_visible: () => true,
  ticket_packages: () => true,
};

const FEATURE_FLAGS = Object.keys(DEFAULT_FEATURES) as FeatureFlag[];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function resolveProduct(value: unknown): Product {
  return value === 'university' || value === 'corporate' ? value : DEFAULT_TENANT_PROFILE.product;
}

function resolveVocabulary(value: unknown, product: Product): Vocabulary {
  const fallback = PRODUCT_VOCABULARY[product];
  const source = isRecord(value) ? value : {};
  const vocabulary = { ...fallback };
  for (const key of Object.keys(fallback) as VocabularyKey[]) {
    const term = source[key];
    if (isRecord(term)) {
      const singular = text(term['singular']);
      const plural = text(term['plural']);
      if (singular && plural) {
        const gender = term['gender'] === 'f' || term['gender'] === 'm' ? term['gender'] : fallback[key].gender;
        vocabulary[key] = { singular, plural, gender };
      }
    }
  }
  return vocabulary;
}

function resolveSso(value: unknown): InstanceSso {
  if (!isRecord(value)) return DEFAULT_SSO;
  return {
    enabled: typeof value['enabled'] === 'boolean' ? value['enabled'] : DEFAULT_SSO.enabled,
    idp: text(value['idp']) ?? DEFAULT_SSO.idp,
    label: text(value['label']) ?? DEFAULT_SSO.label,
  };
}

function resolveBrand(value: unknown): InstanceBrand | null {
  if (!isRecord(value)) return null;
  const str = (key: keyof InstanceBrand) => {
    const field = value[key];
    return typeof field === 'string' ? field : '';
  };
  return {
    name: str('name'),
    logo: str('logo'),
    logo_dark: str('logo_dark'),
    favicon: str('favicon'),
    primary_color: str('primary_color'),
  };
}

function resolveFeatures(value: unknown, user: TenantUser): Features {
  const source = isRecord(value) ? value : null;
  const features = { ...DEFAULT_FEATURES };
  for (const flag of FEATURE_FLAGS) {
    const sent = source?.[flag];
    features[flag] = typeof sent === 'boolean' ? sent : LEGACY_FEATURES[flag](user);
  }
  return features;
}

function resolveDaysToBook(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

/** El perfil que corresponde a un usuario (el `me` guardado o el que acaba de llegar). */
export function resolveTenantProfile(user: TenantUser | null | undefined): TenantProfile {
  if (!user) return DEFAULT_TENANT_PROFILE;
  const instance: Record<string, unknown> = isRecord(user.instance) ? user.instance : {};
  const product = resolveProduct(instance['product']);
  return {
    product,
    brand: resolveBrand(instance['brand']),
    vocabulary: resolveVocabulary(instance['vocabulary'], product),
    sso: resolveSso(instance['sso']),
    features: resolveFeatures(instance['features'], user),
    daysToBook: resolveDaysToBook(instance['days_to_book']),
  };
}
